import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { fmtINR } from "../lib/constants";
import { FileBarChart, AlertOctagon, FileText, Users as UsersIcon, BellRing, CalendarClock, ExternalLink, Download } from "lucide-react";

const REPORTS = [
  { key: "risk", label: "MD Compliance Risk Register", icon: AlertOctagon, desc: "All clients · overdue first" },
  { key: "client_status", label: "Client ECMP Status Report", icon: FileText, desc: "Per client · meeting-ready" },
  { key: "renewal", label: "Renewal & Billing Pipeline", icon: CalendarClock, desc: "Contract end dates + ₹ value" },
  { key: "workload", label: "Team Workload & Accountability", icon: UsersIcon, desc: "Per admin · overdue flags" },
  { key: "alerts", label: "Alert-to-Action Report", icon: BellRing, desc: "Alerts sent vs status" },
  { key: "weekly", label: "Weekly Review Pack", icon: FileBarChart, desc: "Auto every Friday 6 PM" },
];

export default function Reports() {
  const [active, setActive] = useState("risk");
  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Admin Reports</p>
        <h1 className="text-4xl font-bold tracking-tight text-[#0F2A4A]">Reports</h1>
        <p className="text-stone-500 mt-2">6 management reports · all PDF-exportable with PT branding.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.key}
              onClick={() => setActive(r.key)}
              data-testid={`report-tab-${r.key}`}
              className={`text-left p-4 rounded-xl border-2 transition ${
                active === r.key ? "border-[#72BE44] bg-[#72BE44]/5" : "border-stone-200 bg-white hover:border-stone-400"
              }`}
            >
              <Icon className={`w-4 h-4 mb-2 ${active === r.key ? "text-[#72BE44]" : "text-stone-500"}`} />
              <div className="text-sm font-semibold text-[#0F2A4A]">{r.label}</div>
              <div className="text-[10px] text-stone-500 mt-1">{r.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        {active === "risk" && <RiskRegister />}
        {active === "client_status" && <ClientStatus />}
        {active === "renewal" && <RenewalPipeline />}
        {active === "workload" && <Workload />}
        {active === "alerts" && <AlertToAction />}
        {active === "weekly" && <WeeklyPack />}
      </div>
    </div>
  );
}

function bucketColor(b) {
  if (b === "overdue") return "bg-red-100 text-red-800 border-red-300";
  if (b === "30") return "bg-amber-100 text-amber-800 border-amber-300";
  if (b === "90") return "bg-yellow-50 text-yellow-800 border-yellow-200";
  return "bg-emerald-50 text-emerald-800 border-emerald-200";
}

function RiskRegister() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/risk-register").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone-500">Loading...</div>;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <KPI label="Total Overdue" value={data.summary.overdue} color="red" />
        <KPI label="Due in 30 days" value={data.summary.due_30} color="amber" />
        <KPI label="Due in 90 days" value={data.summary.due_90} color="emerald" />
      </div>
      <Table head={["Client", "Project", "Item", "Authority", "Due", "Days", "Status", "Owner"]}>
        {data.rows.map((r) => (
          <tr key={r.id} data-testid={`risk-row-${r.id}`} className={`border-t border-stone-100 ${r.bucket === "overdue" ? "bg-red-50" : r.bucket === "30" ? "bg-amber-50" : ""}`}>
            <td className="px-3 py-2 font-medium">{r.client_name || "—"}</td>
            <td className="px-3 py-2">{r.project_name || "—"}</td>
            <td className="px-3 py-2"><b>{r.type}</b> — {r.title}</td>
            <td className="px-3 py-2 text-xs">{r.authority || "—"}</td>
            <td className="px-3 py-2 text-xs font-mono">{(r.due_date || "").slice(0, 10)}</td>
            <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${bucketColor(r.bucket)}`}>{r.days_remaining}d</span></td>
            <td className="px-3 py-2 text-xs">{r.status}</td>
            <td className="px-3 py-2 text-xs">{r.assigned_name || "—"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function ClientStatus() {
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/users?role=client").then(r => { setClients(r.data); if (r.data[0]) setCid(r.data[0].id); }); }, []);
  useEffect(() => { if (cid) api.get(`/reports/client-status/${cid}`).then(r => setData(r.data)); }, [cid]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Client</label>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="cs-client" className="px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm">
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {!data ? <div className="text-stone-500">Loading...</div> : (
        <div className="space-y-5">
          <div className="grid grid-cols-4 gap-3">
            <KPI label="Active Items" value={data.summary.total_active} color="navy" />
            <KPI label="Overdue" value={data.summary.overdue} color="red" />
            <KPI label="Due ≤30d" value={data.summary.due_30} color="amber" />
            <KPI label="Documents" value={data.documents_count} color="emerald" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Projects</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.projects.map((p) => <div key={p.id} className="border border-stone-200 rounded-lg p-3 text-sm"><b>{p.project_name}</b><div className="text-xs text-stone-500">{p.service_type} · {p.project_location}</div></div>)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Next 5 Upcoming Deadlines</div>
            <Table head={["Due", "Item", "Authority", "Status"]}>
              {data.upcoming_5.map((c) => (
                <tr key={c.id} className="border-t border-stone-100">
                  <td className="px-3 py-2 text-xs font-mono">{c.due_date}</td>
                  <td className="px-3 py-2"><b>{c.type}</b> — {c.title}</td>
                  <td className="px-3 py-2 text-xs">{c.authority || "—"}</td>
                  <td className="px-3 py-2 text-xs">{c.status}</td>
                </tr>
              ))}
            </Table>
          </div>
          <a href={`${API}/reports/meeting-report/${cid}/pdf`} target="_blank" rel="noreferrer" data-testid="cs-download-pdf" className="inline-flex items-center gap-2 px-4 py-2 bg-[#72BE44] hover:bg-[#5fa036] text-white rounded-lg text-sm font-medium">
            <Download className="w-4 h-4" /> Download Branded PDF
          </a>
        </div>
      )}
    </div>
  );
}

function RenewalPipeline() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/renewal-pipeline").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone-500">Loading...</div>;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <KPI label="Total Annual Contracted Value" value={fmtINR(data.total_annual_value)} color="navy" />
        <KPI label="Ending in ≤3 months" value={data.ending_soon_count} color="amber" />
        <KPI label="Total Clients" value={data.rows.length} color="emerald" />
      </div>
      <Table head={["Client", "Contract Start", "Contract End", "Months Remaining", "Annual Fee", "Notes"]}>
        {data.rows.map((r) => (
          <tr key={r.id} className={`border-t border-stone-100 ${r.months_remaining !== null && r.months_remaining <= 3 ? "bg-amber-50" : ""}`}>
            <td className="px-3 py-2 font-medium">{r.name}</td>
            <td className="px-3 py-2 text-xs font-mono">{r.contract_start || "—"}</td>
            <td className="px-3 py-2 text-xs font-mono">{r.contract_end || "—"}</td>
            <td className="px-3 py-2 text-xs">{r.months_remaining ?? "—"}</td>
            <td className="px-3 py-2 text-xs font-semibold">{fmtINR(r.annual_fee)}</td>
            <td className="px-3 py-2 text-xs text-stone-500">{r.billing_notes || ""}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Workload() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/workload").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone-500">Loading...</div>;
  return (
    <Table head={["Admin", "Active Items", "Due ≤30 days", "Overdue", "Closed (90d)", "Avg Days to Close"]}>
      {data.rows.map((r) => (
        <tr key={r.owner_id} data-testid={`wl-row-${r.owner_id}`} className={`border-t border-stone-100 ${r.flag ? "bg-red-50" : ""}`}>
          <td className="px-3 py-2 font-medium">{r.owner_name}</td>
          <td className="px-3 py-2">{r.total_active}</td>
          <td className="px-3 py-2">{r.due_30}</td>
          <td className={`px-3 py-2 ${r.overdue > 3 ? "text-red-700 font-bold" : ""}`}>{r.overdue}</td>
          <td className="px-3 py-2">{r.closed_recent}</td>
          <td className="px-3 py-2">{r.avg_days_to_close ?? "—"}</td>
        </tr>
      ))}
    </Table>
  );
}

function AlertToAction() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/alert-to-action").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone-500">Loading...</div>;
  return (
    <Table head={["Sent", "Client", "Item", "Bucket", "Current Status", "Stage"]}>
      {data.rows.map((r) => (
        <tr key={r.id} className={`border-t border-stone-100 ${r.flag ? "bg-red-50" : ""}`}>
          <td className="px-3 py-2 text-xs font-mono">{(r.sent_at || "").slice(0, 10)}</td>
          <td className="px-3 py-2">{r.client_name || "—"}</td>
          <td className="px-3 py-2"><b>{r.type}</b> — {r.title}</td>
          <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.bucket === "overdue" ? "bg-red-200 text-red-900" : r.bucket === "30_day" ? "bg-amber-200 text-amber-900" : "bg-yellow-100 text-yellow-800"}`}>{r.bucket}</span></td>
          <td className="px-3 py-2 text-xs">{r.current_status || "—"}</td>
          <td className="px-3 py-2 text-xs">{r.current_stage || "—"}</td>
        </tr>
      ))}
    </Table>
  );
}

function WeeklyPack() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/weekly").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone-500">Loading...</div>;
  return (
    <div className="space-y-5">
      <div className="text-xs text-stone-500">Period: {data.period_start} → {data.period_end}</div>
      <div className="grid grid-cols-4 gap-3">
        <KPI label="New Items" value={data.new_items_count} color="navy" />
        <KPI label="Closed Items" value={data.closed_items_count} color="emerald" />
        <KPI label="Alerts Sent" value={data.alerts_sent_count} color="amber" />
        <KPI label="Overdue" value={data.overdue_count} color="red" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Top 5 Highest-Risk Items</div>
        <Table head={["Due", "Item", "Project", "Days"]}>
          {data.top_5_risk.map((r) => (
            <tr key={r.id} className="border-t border-stone-100">
              <td className="px-3 py-2 text-xs font-mono">{r.due_date}</td>
              <td className="px-3 py-2"><b>{r.type}</b> — {r.title}</td>
              <td className="px-3 py-2 text-xs">{r.project_name || "—"}</td>
              <td className={`px-3 py-2 text-xs font-bold ${r.days < 0 ? "text-red-700" : r.days <= 30 ? "text-amber-700" : ""}`}>{r.days}d</td>
            </tr>
          ))}
        </Table>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Contracts Ending in 90 days</div>
        {data.ending_contracts.length === 0 ? <div className="text-stone-500 text-sm">None</div> :
          <Table head={["Client", "End Date", "Months Left", "Annual Fee"]}>
            {data.ending_contracts.map((c) => (
              <tr key={c.id} className="border-t border-stone-100">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-xs font-mono">{c.contract_end}</td>
                <td className="px-3 py-2">{c.months_remaining}</td>
                <td className="px-3 py-2 text-xs font-semibold">{fmtINR(c.annual_fee)}</td>
              </tr>
            ))}
          </Table>
        }
      </div>
    </div>
  );
}

function KPI({ label, value, color }) {
  const colors = {
    red: "border-l-red-500",
    amber: "border-l-amber-500",
    emerald: "border-l-[#72BE44]",
    navy: "border-l-[#0F2A4A]",
  };
  return (
    <div className={`bg-white border border-stone-200 rounded-xl p-4 border-l-4 ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-[#0F2A4A] mt-1">{value}</div>
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
          <tr>{head.map((h) => <th key={h} className="text-left px-3 py-2.5 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
